// @flow
import React, { Fragment, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import split from 'ramda/es/split';
import last from 'ramda/es/last';
import isEmpty from 'ramda/es/isEmpty';

import RowLayout, { Header } from 'app/common/layouts/RowLayout';
import { PlainInputSearch } from 'app/common/ui/PlainInput';
import { OpenFilterButton, ButtonsBar } from 'app/common/ui/Button';
import { CatalogSection, CatalogContainer, CatalogProduct } from 'app/common/layouts/catalog';
// $FlowFixMe
import { сategoriesFetch, SHOP_CATEGORIES_CLEAR } from 'app/common/actions/shop/catalog/categories';
// $FlowFixMe
import { productsFetchSearch, SHOP_PRODUCTS_CLEAR } from 'app/common/actions/shop/catalog/products';
import action from 'app/common/lib/action';
import Loading from 'app/common/components/Loader';
import EmptyWarn from 'app/common/layouts/EmptyWarn';
import { LocalBreadcrumb } from 'app/common/components/Shop';
// $FlowFixMe
import { SHOP_SET_FILTER, SHOP_CLEAR_SEARCH } from 'app/common/actions/shop/catalog/filter';

import selectCategories from './selectCategories';
import selectProducts from './selectProducts';
import styles from './CatalogItems.module.scss';;


type Props = {
  baseUrl: string,
  params: {
    splat?: string,
    productSlug?: string,
  },
  push: (path: string) => void,
}

const CatalogItems = ({ baseUrl, params, push }: Props) => {
  const { splat } = params;
  const dispatch = useDispatch();

  // базовые пути для товара и раздела
  const [categoryBaseUrl, productBaseUrl] = useMemo(() => {
    const categories = splat ? `/${splat}` : '';
    return [
      `${baseUrl}/category${categories}`,
      `${baseUrl}/product${categories}`,
    ];
  }, [splat, baseUrl]);

  // Категории
  const categories = useSelector(selectCategories);

  // Товары
  const products = useSelector(selectProducts);

  // текущая категория
  const { name } = useSelector(state => state.shop.catalog.categories.current, shallowEqual);

  // индикация загрузки
  const loadingCategories = useSelector(state => state.shop.catalog.categories.loading, shallowEqual);
  const loadingProducts = useSelector(state => state.shop.catalog.products.loading, shallowEqual);
  const emptyCategories = isEmpty(categories);
  const emptyProducts = isEmpty(products);

  // текущий путь
  const categoriesPathArr: Array<string> = useMemo(() => (
    splat ? split('/', splat) : []
  ), [splat]);

  // поиск
  const searchString = useSelector(state => state.shop.catalog.filter.searchString, shallowEqual);
  const handleClearSearch = useCallback((e) => {
    e.preventDefault();
    dispatch(action(SHOP_CLEAR_SEARCH));
  }, [dispatch]);
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    const { value } = e.currentTarget;
    dispatch(action(SHOP_SET_FILTER, { searchString: value }));
  }, [dispatch]);

  // функция запроса категорий и товаров каталога
  const handleRequest = useCallback(async (skipCategories) => {
    if (!skipCategories) {
      const res = await dispatch(сategoriesFetch(splat ? last(categoriesPathArr) : null));
      if (res.errStatus === 404) {
        push('/shop');
        return;
      }
    }
    if (splat) {
      const res2 = await dispatch(productsFetchSearch(last(categoriesPathArr), searchString));
      if (res2.errStatus === 404) {
        push('/shop');
      }
    }
  }, [dispatch, categoriesPathArr, searchString, splat, push]); // eslint-disable-line react-hooks/exhaustive-deps

  // Запрос товаров
  useEffect(() => {
    handleRequest();
    return () => {
      dispatch(action(SHOP_CATEGORIES_CLEAR));
      dispatch(action(SHOP_PRODUCTS_CLEAR));
    };
  }, [dispatch, handleRequest]);

  // не в процессе загрузки и нет ни товаров ни разделов
  const nothing = (splat
    ? (!loadingCategories && emptyCategories) && (!loadingProducts && emptyProducts)
    : false // на первой странице всегда что-то есть
  );

  return (
    <Fragment>
      <RowLayout>
        <Header>{name || 'Каталог'}</Header>
      </RowLayout>
      <RowLayout className={styles.layoutLine2}>
        <ButtonsBar>
          <PlainInputSearch
            placeholder="Поиск"
            handleClearSearch={handleClearSearch}
            handleSearch={handleSearch}
            searchString={searchString}
            disabled={loadingCategories || loadingProducts}
          />
          <OpenFilterButton disabled onClick={() => {}} />
        </ButtonsBar>
      </RowLayout>
      <RowLayout>

        {splat
          && <LocalBreadcrumb
            lastTitle={name}
            baseUrl={`${baseUrl}/category`}
            splat={splat}
            removeLast
          />}

        {/* Индикация загрузки, когда не было данных в предыдущей загрузке */}
        {(loadingCategories || loadingProducts)
          && emptyCategories
          && emptyProducts
          && <Loading />}

        {/* Render товаров и разделов */}
        {(!emptyCategories || !emptyProducts)
          && <Fragment>
            <CatalogContainer>
              {categories.map(({ slug, title, imgSrc }) => (
                <CatalogSection
                  key={slug}
                  title={title}
                  imgSrc={imgSrc}
                  loading={loadingCategories}
                  link={`${categoryBaseUrl}/${slug}`}
                />
              ))}
            </CatalogContainer>
            <CatalogContainer>
              {products.map(({ id, title, imgSrc, slug, price }) => (
                <CatalogProduct
                  key={id}
                  title={title}
                  price={price}
                  imgSrc={imgSrc}
                  loading={loadingProducts}
                  link={`${productBaseUrl}/${slug}`}
                />
              ))}
            </CatalogContainer>
          </Fragment>}

        {/* Отображение заглушки, когда нет товаров/разделов и процесс загрузки закончен */}
        {nothing
          && <EmptyWarn>{'Нет записей, удовлетворяющих условиям'}</EmptyWarn>}
      </RowLayout>
    </Fragment>
  );
};

export default CatalogItems;
